// ─────────────────────────────────────────────────────────────────────────────
// Artist CNTRL, secure token storage (React port of m-app.js acSecure ~112-352).
//
// On iOS, plain localStorage lives in the WebView's WebKit cache, unencrypted at
// rest and included in device backups. This layer routes the auth Bearer token
// (and push device token) through, in priority order:
//   1. a community SecureStorage/SecureStoragePlugin (Keychain) if installed,
//   2. @capacitor/preferences (native UserDefaults), already installed,
//   3. localStorage, web fallback (unchanged behaviour off-device).
//
// Reads are SYNC (every call site assumes it): an in-memory cache is primed from
// localStorage at module load, then reconciled async with the native store.
// Writes mirror to localStorage (sync) AND native (async). localStorage is treated
// as AUTHORITATIVE on conflict, this is the fix for the account-switch race where
// a slow native remove() would otherwise restore the previous session.
// ─────────────────────────────────────────────────────────────────────────────

import { getPlugin, isPluginAvailable } from './capacitor';

/** Canonical bearer-token key. Source of truth: cntrl-token-shim.js / m-app.js. */
export const TOKEN_KEY = 'cntrl_auth_token';
/** Push device token key (m-app.js PUSH_TOKEN_KEY). */
export const PUSH_TOKEN_KEY = 'ac_push_device_token';
/** Logout marker, survives navigation; forces native tokens cleared on boot. */
const LOGOUT_MARKER = 'ac:logged_out_at';

const HYDRATE_KEYS = [TOKEN_KEY, PUSH_TOKEN_KEY];

const mem: Record<string, string> = {};
let backendName: 'localStorage' | 'keychain' | 'userDefaults' = 'localStorage';

// Keys written via secureSet/secureRemove AFTER hydrate began. The async native
// reconcile must NOT clobber these with a value it read BEFORE the write (e.g. a
// passkey login that completes mid-hydrate, or a sign-out remove).
const dirty = new Set<string>();

// Resolves once the async native (Keychain/Preferences) hydration has settled,
// so a boot auth-gate can wait for the real token before deciding to redirect to
// login. On web (localStorage backend) it resolves immediately. The token may
// only exist in the Keychain (e.g. after delete+reinstall, where iOS preserves
// Keychain but wipes localStorage); without awaiting this, a sync getToken()
// would read empty and bounce a logged-in user to login.
let _resolveReady: () => void = () => {};
let _readyFired = false;
export const secureReady: Promise<void> = new Promise<void>((res) => { _resolveReady = res; });
// Idempotent + GUARANTEED resolver. secureReady gates the boot screen (main.tsx),
// so if it never resolves the app hangs on the loader forever with no recovery.
// Every hydrate path calls fireReady(); the watchdog guarantees resolution even
// if the hydrate IIFE throws synchronously or a native call stalls.
function fireReady(): void { if (!_readyFired) { _readyFired = true; _resolveReady(); } }
try { setTimeout(fireReady, 4000); } catch { /* ignore */ }

/* eslint-disable @typescript-eslint/no-explicit-any */
function secureStoragePlugin(): any | null {
  if (isPluginAvailable('SecureStoragePlugin')) { const p = getPlugin('SecureStoragePlugin'); if (p) return p; }
  if (isPluginAvailable('SecureStorage')) { const p = getPlugin('SecureStorage'); if (p) return p; }
  return null;
}
function preferencesPlugin(): any | null {
  return getPlugin('Preferences');
}

function backendGet(key: string): Promise<string> {
  const ss = secureStoragePlugin();
  if (ss && ss.get) return ss.get({ key }).then((r: any) => (r && r.value) || '').catch(() => '');
  const pf = preferencesPlugin();
  if (pf && pf.get) return pf.get({ key }).then((r: any) => (r && r.value) || '').catch(() => '');
  try { return Promise.resolve(localStorage.getItem(key) || ''); } catch { return Promise.resolve(''); }
}
function backendSet(key: string, value: string): Promise<void> {
  // Always mirror to localStorage synchronously, the only store the next sync
  // boot read can see before the async native write flushes.
  try { localStorage.setItem(key, String(value || '')); } catch { /* ignore */ }
  const ss = secureStoragePlugin();
  if (ss && ss.set) return ss.set({ key, value: String(value) }).catch(() => {});
  const pf = preferencesPlugin();
  if (pf && pf.set) return pf.set({ key, value: String(value) }).catch(() => {});
  return Promise.resolve();
}
function backendRemove(key: string): Promise<void> {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
  const ss = secureStoragePlugin();
  if (ss && ss.remove) return ss.remove({ key }).catch(() => {});
  const pf = preferencesPlugin();
  if (pf && pf.remove) return pf.remove({ key }).catch(() => {});
  return Promise.resolve();
}

(function hydrate() {
  try {
    if (secureStoragePlugin()) backendName = 'keychain';
    else if (preferencesPlugin()) backendName = 'userDefaults';

    // Logout-race fix: a fresh logout marker forces tokens cleared before hydration.
    const raw = (() => { try { return localStorage.getItem(LOGOUT_MARKER); } catch { return null; } })();
    if (raw) {
      const ts = parseInt(raw, 10);
      // 30s window: a cold WebView reload after sign-out can take several seconds;
      // if the marker expires first, the async reconcile below would restore a
      // still-present Keychain token (= logout no-ops).
      if (!isNaN(ts) && (Date.now() - ts) < 30000) {
        // Clear sync stores immediately so getToken() reads empty, AND await the
        // native (Keychain/Preferences) removal before resolving secureReady, so
        // nothing can read a stale native token after this point.
        HYDRATE_KEYS.forEach((k) => {
          try { localStorage.removeItem(k); } catch { /* ignore */ }
          mem[k] = '';
        });
        Promise.all(HYDRATE_KEYS.map((k) => backendRemove(k).catch(() => {})))
          .then(() => {
            try { localStorage.removeItem(LOGOUT_MARKER); } catch { /* ignore */ }
            fireReady(); // resolve secureReady, else the boot gate (main.tsx) hangs.
          })
          .catch(fireReady);
        return;
      }
      try { localStorage.removeItem(LOGOUT_MARKER); } catch { /* ignore */ }
    }

    // Sync prime from localStorage so getToken() works during boot.
    HYDRATE_KEYS.forEach((k) => {
      try { mem[k] = localStorage.getItem(k) || ''; } catch { mem[k] = ''; }
    });
    if (backendName === 'localStorage') { fireReady(); return; }

    // Async reconcile with native. localStorage authoritative on conflict.
    // fireReady() fires once ALL keys have settled so the boot gate sees the real
    // (possibly Keychain-only) token before deciding. Skips keys written since
    // hydrate began (dirty) so an in-flight reconcile can't clobber a fresh login.
    Promise.all(HYDRATE_KEYS.map((k) =>
      backendGet(k).then((nativeVal) => {
        if (dirty.has(k)) return; // a secureSet/secureRemove already won, don't overwrite
        let lsVal = '';
        try { lsVal = localStorage.getItem(k) || ''; } catch { /* ignore */ }
        if (nativeVal && !lsVal) {
          mem[k] = nativeVal;
          try { localStorage.setItem(k, nativeVal); } catch { /* ignore */ }
        } else if (!nativeVal && lsVal) {
          mem[k] = lsVal;
          void backendSet(k, lsVal);
        } else if (lsVal && nativeVal && lsVal !== nativeVal) {
          mem[k] = lsVal;            // localStorage wins (account-switch race)
          void backendSet(k, lsVal);
        }
      }).catch(() => {}),
    )).then(() => fireReady()).catch(fireReady);
  } catch {
    // Never let a synchronous throw strand boot, resolve and proceed off mem.
    fireReady();
  }
})();

/** Sync read from the in-memory cache (primed from localStorage + native). */
export function secureGet(key: string): string {
  return mem[key] || '';
}
/** Write to memory (sync) + localStorage + native (async, fire-and-forget). */
export function secureSet(key: string, value: string): Promise<void> {
  mem[key] = value || '';
  dirty.add(key); // protect against a mid-boot reconcile clobbering this write
  // A real token write invalidates any pending logout intent, otherwise a reload
  // within the marker window would wipe the just-set token (bounce to login).
  if (key === TOKEN_KEY && value) {
    try { localStorage.removeItem(LOGOUT_MARKER); } catch { /* ignore */ }
  }
  return backendSet(key, value);
}
/** Remove from memory + localStorage + native. */
export function secureRemove(key: string): Promise<void> {
  mem[key] = '';
  dirty.add(key); // a removal must not be undone by an in-flight reconcile
  return backendRemove(key);
}
/** Write the logout marker so the NEXT boot force-clears native tokens. */
export function markLoggedOut(): void {
  try { localStorage.setItem(LOGOUT_MARKER, String(Date.now())); } catch { /* ignore */ }
}
